import type {
  LoginResponse,
  RefreshResponse,
  User,
  SessionsResponse,
  AuthErrorCode,
} from '@/types';
import { AuthApiError } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
  skipAutoRefresh?: boolean;
}

const GENERIC_ERROR_MESSAGE = 'Algo deu errado. Tente novamente.';
const SERVER_UNAVAILABLE_MESSAGE =
  'Serviço temporariamente indisponível. Tente novamente em instantes.';

const AUTH_CODE_MESSAGES: Record<AuthErrorCode, string> = {
  INVALID_CREDENTIALS: 'E-mail ou senha inválidos.',
  ACCOUNT_LOCKED: 'Sua conta foi bloqueada por excesso de tentativas.',
  ACCOUNT_BLOCKED: 'Sua conta está bloqueada.',
  RATE_LIMIT: 'Muitas tentativas. Tente novamente mais tarde.',
  MFA_REQUIRED: 'Verificação em duas etapas necessária.',
  INVALID_TOKEN: 'Sua sessão é inválida. Entre novamente.',
  TOKEN_EXPIRED: 'Sua sessão expirou. Entre novamente.',
  EMAIL_NOT_VERIFIED: 'Verifique seu e-mail para ativar sua conta.',
  INVALID_2FA_CODE: 'Código de verificação inválido.',
  INVALID_BACKUP_CODE: 'Código de backup inválido.',
  USER_NOT_FOUND: 'Usuário não encontrado.',
  EMAIL_ALREADY_EXISTS: 'Este e-mail já está cadastrado na plataforma.',
  WEAK_PASSWORD: 'A senha informada é fraca. Crie uma senha combinando maiúsculas, minúsculas, números e símbolos.',
  VALIDATION_ERROR: 'Verifique os dados informados e tente novamente.',
  AUTH_INVALID_VERIFICATION_TOKEN: 'Token de verificação inválido ou expirado.',
  UNKNOWN_ERROR: GENERIC_ERROR_MESSAGE,
};

export function formatFriendlyAuthMessage(rawMessage?: unknown, code?: string, status?: number): string {
  if (!rawMessage) {
    if (code && AUTH_CODE_MESSAGES[code as AuthErrorCode]) {
      return AUTH_CODE_MESSAGES[code as AuthErrorCode];
    }
    return status && status >= 500 ? SERVER_UNAVAILABLE_MESSAGE : GENERIC_ERROR_MESSAGE;
  }

  const rawStr = Array.isArray(rawMessage)
    ? rawMessage.join('. ')
    : typeof rawMessage === 'object'
    ? JSON.stringify(rawMessage)
    : String(rawMessage);

  const msg = rawStr.trim();

  // Mapeamentos detalhados em Português:
  if (
    msg.includes('Invalid credentials') ||
    msg.includes('AUTH_INVALID_CREDENTIALS') ||
    code === 'INVALID_CREDENTIALS' ||
    msg === 'Unauthorized' ||
    (status === 401 && !msg)
  ) {
    return 'E-mail ou senha incorretos. Verifique seus dados de acesso.';
  }
  if (
    msg.includes('Account is locked') ||
    msg.includes('AUTH_ACCOUNT_LOCKED') ||
    code === 'ACCOUNT_LOCKED' ||
    status === 423
  ) {
    return 'Sua conta foi temporariamente bloqueada por segurança após múltiplas tentativas. Tente novamente mais tarde.';
  }
  if (
    msg.includes('Account blocked') ||
    msg.includes('AUTH_ACCOUNT_BLOCKED') ||
    code === 'ACCOUNT_BLOCKED'
  ) {
    return 'Sua conta está bloqueada pela administração. Entre em contato com o suporte da Medikei.';
  }
  if (
    msg.includes('Account is') ||
    msg.includes('AUTH_ACCOUNT_INACTIVE') ||
    code === 'ACCOUNT_INACTIVE'
  ) {
    return 'Esta conta está inativa ou aguardando moderação.';
  }
  if (
    msg.includes('Rate limit exceeded') ||
    msg.includes('AUTH_RATE_LIMIT_EXCEEDED') ||
    code === 'RATE_LIMIT' ||
    status === 429
  ) {
    return 'Muitas tentativas de acesso. Aguarde alguns instantes antes de tentar novamente.';
  }
  if (
    msg.includes('User not found') ||
    msg.includes('AUTH_USER_NOT_FOUND') ||
    code === 'USER_NOT_FOUND'
  ) {
    return 'Usuário não encontrado. Verifique o e-mail digitado ou crie uma conta.';
  }
  if (
    msg.includes('Session expired') ||
    msg.includes('AUTH_SESSION_EXPIRED') ||
    code === 'TOKEN_EXPIRED'
  ) {
    return 'Sua sessão expirou. Faça login novamente.';
  }
  if (msg.includes('Email already registered') || msg.includes('AUTH_EMAIL_ALREADY_EXISTS') || code === 'EMAIL_ALREADY_EXISTS') {
    return 'Este e-mail já está cadastrado na plataforma. Caso já possua uma conta, clique em "Fazer login" ou utilize a recuperação de senha.';
  }
  if (
    msg.includes('Phone already registered') ||
    msg.includes('AUTH_PHONE_ALREADY_EXISTS') ||
    (msg.includes('telefone') && msg.includes('vinculado')) ||
    (msg.includes('telefone') && msg.includes('cadastrado'))
  ) {
    return 'Este número de telefone/WhatsApp já está vinculado a outra conta. Informe outro número ou acesse sua conta existente.';
  }
  if (
    msg.includes('CPF already registered') ||
    msg.includes('AUTH_CPF_ALREADY_EXISTS') ||
    (msg.includes('CPF') && msg.includes('cadastrado'))
  ) {
    return 'Este CPF já está cadastrado em outra conta na plataforma. Não é possível criar contas duplicadas com o mesmo CPF.';
  }
  if (
    msg.includes('CPF is invalid') ||
    msg.includes('AUTH_INVALID_CPF') ||
    msg.includes('CPF inválido') ||
    msg.includes('format XXX.XXX.XXX-XX')
  ) {
    return 'O CPF informado é inválido. Verifique se digitou os 11 números corretamente.';
  }
  if (msg.includes('termsConsent') || msg.includes('acceptTerms') || msg.includes('Termos de Uso')) {
    return 'Você precisa concordar com os Termos de Uso e a Política de Privacidade para concluir o cadastro.';
  }
  if (msg.includes('lgpdConsent')) {
    return 'É obrigatório aceitar o tratamento de dados de saúde conforme a LGPD para criar a conta.';
  }
  if (msg.includes('Password must be at least 8 characters') || msg.includes('must be at least 8 characters')) {
    return 'A senha é muito curta. Crie uma senha com no mínimo 8 caracteres.';
  }
  if (msg.includes('Password must contain at least one lowercase') || msg.includes('must contain lowercase')) {
    return 'A senha deve conter pelo menos uma letra minúscula (a-z).';
  }
  if (msg.includes('Password must contain at least one uppercase') || msg.includes('must contain uppercase')) {
    return 'A senha deve conter pelo menos uma letra maiúscula (A-Z).';
  }
  if (msg.includes('Password must contain at least one number') || msg.includes('must contain number')) {
    return 'A senha deve conter pelo menos um número (0-9).';
  }
  if (msg.includes('Password must contain at least one special') || msg.includes('must contain special')) {
    return 'A senha deve conter pelo menos um caractere especial (!@#$%&*).';
  }
  if (msg.includes('Password is too weak') || code === 'WEAK_PASSWORD') {
    return 'A senha informada é fraca. Crie uma senha combinando letras maiúsculas, minúsculas, números e caracteres especiais.';
  }
  if (msg.includes('Birth date') || msg.includes('birth date') || msg.includes('data de nascimento') || msg.includes('Data de nascimento')) {
    return 'Data de nascimento inválida. Verifique o dia, mês e ano informados (não pode ser uma data futura).';
  }
  if (msg.includes('Primary specialty not found') || msg.includes('specialty not found')) {
    return 'A especialidade médica selecionada não foi encontrada. Por favor, selecione uma especialidade válida da lista.';
  }
  if (msg.includes('Please provide a valid email') || msg.includes('email must be an email')) {
    return 'O formato do e-mail é inválido. Digite um e-mail válido (ex.: seu.nome@dominio.com).';
  }
  if (msg.includes('Phone must be in format')) {
    return 'O número de telefone deve incluir o DDD e estar no formato (XX) 9XXXX-XXXX.';
  }

  return msg;
}

async function parseAuthError(response: Response): Promise<AuthApiError> {
  const status = response.status;
  let errorCode: AuthErrorCode = 'UNKNOWN_ERROR';
  let message = GENERIC_ERROR_MESSAGE;
  let retryAfter: number | undefined;

  if (status === 429) {
    errorCode = 'RATE_LIMIT';
    message = AUTH_CODE_MESSAGES.RATE_LIMIT;
    const retryHeader = response.headers.get('Retry-After');
    retryAfter = retryHeader ? parseInt(retryHeader, 10) : undefined;
  } else if (status === 401) {
    errorCode = 'INVALID_CREDENTIALS';
    message = AUTH_CODE_MESSAGES.INVALID_CREDENTIALS;
  } else if (status === 403) {
    errorCode = 'INVALID_TOKEN';
    message = AUTH_CODE_MESSAGES.INVALID_TOKEN;
  } else if (status === 423) {
    errorCode = 'ACCOUNT_LOCKED';
    message = AUTH_CODE_MESSAGES.ACCOUNT_LOCKED;
  }

  const isJsonResponse = (response.headers.get('content-type') || '').includes(
    'application/json'
  );

  let errorData: any;

  if (isJsonResponse) {
    try {
      errorData = await response.json();
    } catch {
      // Ignore JSON parse errors
    }
  }

  const apiCode = (errorData?.error?.code || errorData?.code) as AuthErrorCode | undefined;
  const rawApiMessage = errorData?.error?.message || errorData?.message;

  if (rawApiMessage) {
    message = formatFriendlyAuthMessage(rawApiMessage, apiCode, status);
    if (apiCode) {
      errorCode = apiCode;
    }
  } else if (apiCode && AUTH_CODE_MESSAGES[apiCode]) {
    errorCode = apiCode;
    message = AUTH_CODE_MESSAGES[apiCode];
  } else if (status >= 500) {
    message = SERVER_UNAVAILABLE_MESSAGE;
  }

  if (errorData?.error?.retryAfterMs || errorData?.retryAfterMs) {
    retryAfter = errorData.error?.retryAfterMs || errorData.retryAfterMs;
  }

  return new AuthApiError(errorCode, message, retryAfter);
}

class HttpClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.loadAccessTokenFromStorage();
  }

  private loadAccessTokenFromStorage(): void {
    this.accessToken = localStorage.getItem('accessToken');
  }

  saveAccessToken(accessToken: string): void {
    this.accessToken = accessToken;
    localStorage.setItem('accessToken', accessToken);
  }

  private clearAccessToken(): void {
    this.accessToken = null;
    localStorage.removeItem('accessToken');
  }

  private subscribeTokenRefresh(callback: (token: string) => void): void {
    this.refreshSubscribers.push(callback);
  }

  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
  }

  private onSessionExpired: (() => void) | null = null;

  setSessionExpiredHandler(handler: () => void): void {
    this.onSessionExpired = handler;
  }

  async refreshAccessToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.subscribeTokenRefresh((token) => resolve(token));
      });
    }

    this.isRefreshing = true;

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        this.clearAccessToken();
        this.onSessionExpired?.();
        return null;
      }

      const data = await response.json();

      if (data.success && data.data?.accessToken) {
        this.saveAccessToken(data.data.accessToken);
        this.onTokenRefreshed(data.data.accessToken);
        return data.data.accessToken;
      }

      throw new Error('Refresh failed');
    } catch {
      this.clearAccessToken();
      this.onSessionExpired?.();
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  private getCsrfToken(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf-token' && value) {
        return value;
      }
    }
    return null;
  }

  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      requiresAuth = true,
      skipAutoRefresh = false,
      ...fetchOptions
    } = options;

    const url = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl}${endpoint}`;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (requiresAuth && this.accessToken) {
      requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      requestHeaders['X-CSRF-Token'] = csrfToken;
    }

    let response = await fetch(url, {
      ...fetchOptions,
      headers: requestHeaders,
      credentials: 'include',
    });

    if (response.status === 401 && requiresAuth && !skipAutoRefresh) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        requestHeaders['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          ...fetchOptions,
          headers: requestHeaders,
          credentials: 'include',
        });
      } else {
        const error = await parseAuthError(response).catch(() => ({
          code: 'UNAUTHORIZED',
          message: 'Session expired',
          statusCode: 401,
        }));
        throw error;
      }
    }

    if (!response.ok) {
      const error = await parseAuthError(response);
      console.warn(`[HttpClient API Error] ${fetchOptions.method || 'GET'} ${url} - Status: ${response.status}`, error);
      throw error;
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = {
        error: true,
        message: `${response.status}: Serviço temporariamente indisponível`,
      };
    }
    if (data && typeof data === 'object' && 'meta' in data && 'data' in data) {
      return data as T;
    }
    if (
      data &&
      typeof data === 'object' &&
      'data' in data &&
      (data as { data?: unknown }).data
    ) {
      return (data as { data: T }).data;
    }
    return data as T;
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(
    endpoint: string,
    options?: RequestOptions & { data?: unknown }
  ): Promise<T> {
    let body = options?.body;
    if (options?.data !== undefined) {
      body = JSON.stringify(options.data);
    }
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      ...(body !== undefined ? { body } : {}),
    });
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  logout(): void {
    this.clearAccessToken();
  }

  getSessionIdFromToken(): string | null {
    const token = this.accessToken;
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = JSON.parse(atob(payload));
      return decoded.sessionId || null;
    } catch {
      return null;
    }
  }
}

export const httpClient = new HttpClient(API_BASE_URL);

export { AuthApiError };

export const publicApi = {
  getHome: (): Promise<PublicHomeData> =>
    httpClient.get<PublicHomeData>('/public/home', {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  search: (query: string): Promise<PublicSearchData> =>
    httpClient.get<PublicSearchData>(`/public/search?q=${encodeURIComponent(query)}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),
};

export interface PublicHomeData {
  hero: {
    headline: string;
    subheadline: string;
    ctaText: string;
    footerLine: string;
  };
  trustBar: {
    items: Array<{ icon: string; text: string }>;
  };
  howItWorks: {
    steps: Array<{ step: number; title: string; description: string; icon: string }>;
  };
  specialties: {
    specialties: Array<{ id: string; name: string; icon: string }>;
  };
  featuredDoctors: {
    doctors: Array<{
      id: string;
      displayName: string;
      specialty: string;
      crm: string;
      crmState: string;
      rating: number;
      reviewCount: number;
      photoUrl: string | null;
    }>;
  };
  testimonials: {
    testimonials: Array<{
      id: string;
      rating: number;
      comment: string;
      patientName: string;
      source: string;
      avatarUrl: string | null;
    }>;
  };
  aiSection: {
    title: string;
    body: string;
    features: string[];
  };
  faq: {
    items: Array<{ question: string; answer: string }>;
  };
  cta: {
    title: string;
    subtitle: string;
    ctaText: string;
    footerLine: string;
  };
  stats: {
    totalDoctors: number;
    totalSpecialties: number;
    averageRating: number;
    totalAppointments: number;
  };
}

export interface PublicSearchData {
  query: string;
  results: Array<{
    id: string;
    type: 'doctor' | 'specialty';
    name: string;
    subtitle: string | null;
    photoUrl: string | null;
  }>;
  total: number;
}

export const authApi = {
  login: (email: string, password: string, captchaToken?: string): Promise<LoginResponse> =>
    httpClient.post<LoginResponse>(
      '/auth/login',
      { email, password, captchaToken },
      { requiresAuth: false, skipAutoRefresh: true }
    ),

  googleLogin: (token: string): Promise<LoginResponse> =>
    httpClient.post<LoginResponse>(
      '/auth/google',
      { token },
      { requiresAuth: false, skipAutoRefresh: true }
    ),

  logout: async (userId: string, sessionId?: string): Promise<void> => {
    try {
      await httpClient.post('/auth/logout', { userId, sessionId }, { requiresAuth: true });
    } catch {
      // Silencia erro no logout para garantir limpeza de sessão sem travamentos
    }
  },

  logoutAll: async (userId: string): Promise<void> => {
    try {
      await httpClient.post('/auth/logout-all', { userId }, { requiresAuth: true });
    } catch {
      // Silencia erro no logout para garantir limpeza de sessão sem travamentos
    }
  },

  refresh: (): Promise<RefreshResponse> =>
    httpClient.post<RefreshResponse>(
      '/auth/refresh',
      {},
      { requiresAuth: false, skipAutoRefresh: true }
    ),

  getMe: (): Promise<User> =>
    httpClient.get<User>('/auth/me'),

  register: (data: {
    name: string;
    email: string;
    password: string;
    role?: string;
    cpf?: string;
    phone?: string;
    birthDate?: string;
    lgpdConsent?: boolean;
    termsConsent?: boolean;
    referralCode?: string;
    captchaToken?: string;
  }): Promise<{ user: User; message: string }> =>
    httpClient.post<{ user: User; message: string }>(
      '/auth/register',
      data,
      { requiresAuth: false }
    ),

  registerDoctor: (data: {
    name: string;
    email: string;
    password: string;
    phone: string;
    cpf: string;
    crm: string;
    crmState: string;
    primarySpecialtyId?: string;
    secondarySpecialtyIds?: string[];
    acceptTerms: boolean;
  }): Promise<{ userId: string }> =>
    httpClient.post<{ userId: string }>(
      '/auth/register/doctor',
      data,
      { requiresAuth: false }
    ),

  verifyEmail: (token: string): Promise<{ message: string }> =>
    httpClient.post<{ message: string }>(
      '/auth/verify-email',
      { token },
      { requiresAuth: false }
    ),

  requestEmailVerification: (email: string): Promise<{ message: string }> =>
    httpClient.post<{ message: string }>(
      '/auth/request-email-verification',
      { email },
      { requiresAuth: false }
    ),

  forgotPassword: (email: string): Promise<{ message: string }> =>
    httpClient.post<{ message: string }>(
      '/auth/forgot-password',
      { email },
      { requiresAuth: false }
    ),

  resetPassword: (token: string, newPassword: string): Promise<{ message: string }> =>
    httpClient.post<{ message: string }>(
      '/auth/reset-password',
      { token, newPassword },
      { requiresAuth: false }
    ),

  verify2FA: (
    pendingSessionId: string,
    userId: string,
    code: string,
    isBackupCode?: boolean
  ): Promise<LoginResponse> =>
    httpClient.post<LoginResponse>(
      '/auth/verify-2fa',
      { pendingSessionId, userId, code, isBackupCode },
      { requiresAuth: false }
    ),

  getSessions: (): Promise<SessionsResponse> =>
    httpClient.get<SessionsResponse>('/auth/sessions'),

  logoutSession: (sessionId: string): Promise<void> =>
    httpClient.post('/auth/logout-session', { sessionId }, { requiresAuth: true }),
};

export interface DoctorListItem {
  id: string;
  crm: string;
  crmState: string;
  bio: string | null;
  experience: number | null;
  status: string;
  profile: {
    displayName: string | null;
    tagline: string | null;
    languages: string[];
    photoUrl: string | null;
  } | null;
  primarySpecialty: {
    id: string;
    name: string;
    icon: string | null;
  } | null;
  statistics: {
    averageRating: number;
    totalRatings: number;
    totalAppointments: number;
  } | null;
  isOnline?: boolean;
  estimatedWait?: number;
  consultationPrice?: number;
  telemedicinePrice?: number;
  inPersonPrice?: number;
  homeCarePrice?: number;
  startingPrice?: number;
  hasTelemedicine?: boolean;
  hasInPerson?: boolean;
  hasHomeCare?: boolean;
  isExpressEnabled?: boolean;
  clinicName?: string | null;
  clinicAddress?: string | null;
  clinicNumber?: string | null;
  clinicComplement?: string | null;
  clinicNeighborhood?: string | null;
  clinicCity?: string | null;
  clinicState?: string | null;
  clinicCep?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  homeCareRadiusKm?: number | null;
  inPersonRadiusKm?: number | null;
  distanceKm?: number | null;
  minAge?: number | null;
  maxAge?: number | null;
  allowedAgeGroups?: string[];
  priorityOrder?: number | null;
  priorityBadge?: string | null;
  isPinned?: boolean;
}

export interface DoctorProfile {
  id?: string;
  userId?: string;
  crm?: string;
  crmState?: string;
  bio?: string | null;
  status?: string;
  displayName: string | null;
  tagline: string | null;
  languages: string[];
  awards: string[];
  publications: string | null;
  photoUrl: string | null;
  coverUrl: string | null;
  hasTelemedicine?: boolean;
  hasInPerson?: boolean;
  hasHomeCare?: boolean;
  clinicName?: string | null;
  clinicAddress?: string | null;
  clinicNumber?: string | null;
  clinicComplement?: string | null;
  clinicNeighborhood?: string | null;
  clinicCity?: string | null;
  clinicState?: string | null;
  clinicCep?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  homeCareRadiusKm?: number | null;
  inPersonRadiusKm?: number | null;
  minAge?: number | null;
  maxAge?: number | null;
  allowedAgeGroups?: string[];
}

export interface DoctorEducation {
  id: string;
  institution: string;
  degree: string;
  field: string | null;
  startDate: Date;
  endDate: Date | null;
}

export interface DoctorExperience {
  id: string;
  title: string;
  company: string;
  location: string | null;
  startDate: Date;
  endDate: Date | null;
  current: boolean;
  description: string | null;
}

export interface DoctorSpecialty {
  id: string;
  doctorId: string;
  specialtyId: string;
  primary: boolean;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  rqe?: string;
  proofDocumentUrl?: string;
  verifiedAt?: string;
  telemedicineEnabled?: boolean;
  inPersonEnabled?: boolean;
  homeCareEnabled?: boolean;
  rejectionReason?: string;
  createdAt: string;
  specialty: Specialty;
}

export interface DoctorAvailabilitySlot {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

export interface DoctorAvailabilityException {
  id: string;
  date: Date;
  isBlocked: boolean;
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
}

export interface DoctorStatistics {
  totalAppointments: number;
  totalPatients: number;
  averageRating: number;
  totalRatings: number;
  responseTime: number | null;
  completionRate: number | null;
}

export interface DoctorDetail extends DoctorListItem {
  minAge?: number | null;
  maxAge?: number | null;
  allowedAgeGroups?: string[];
  experiences?: Array<{
    id: string;
    title: string;
    company: string;
    startDate: Date;
    endDate: Date | null;
  }>;
  education?: Array<{
    id: string;
    institution: string;
    degree: string;
    field: string | null;
  }>;
  ratings?: Array<{
    id: string;
    rating: number;
    comment: string | null;
    createdAt: Date;
    patient: {
      id: string;
      user: {
        id: string;
      };
    };
  }>;
}

export interface PaginatedDoctors {
  data: DoctorListItem[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    pages: number;
  };
}

export interface Specialty {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
}

export interface MarketplaceHomeData {
  specialties: Specialty[];
  featuredDoctors: DoctorListItem[];
  statistics: {
    totalDoctors: number;
    totalSpecialties: number;
    averageRating: number;
    totalConsultations: number;
  };
  faq: Array<{
    question: string;
    answer: string;
  }>;
}

export interface PatientProfile {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  cpf: string | null;
  birthDate: Date | null;
  gender: string | null;
  avatar: string | null;
  role: string;
  status: string;
  acceptedLgpdAt: Date | null;
  healthProfile: {
    weight: number | null;
    height: number | null;
    bmi: number | null;
    bloodType: string | null;
  } | null;
}

export type DocumentType = 'CRM' | 'PHOTO' | 'IDENTITY' | 'SELFIE' | 'CERTIFICATE' | 'DIPLOMA' | 'RQE';
export type DocumentStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface DoctorDocument {
  id: string;
  doctorId: string;
  type: DocumentType;
  fileUrl: string;
  status: DocumentStatus;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DoctorVerification {
  id: string;
  doctorId: string;
  crmVerified: boolean;
  crmVerifiedAt?: string | null;
  documentsVerified: boolean;
  documentsVerifiedAt?: string | null;
  backgroundChecked: boolean;
  backgroundCheckedAt?: string | null;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
}

export const doctorsApi = {
  getDoctors: (params?: {
    specialty?: string;
    language?: string;
    rating?: number;
    search?: string;
    city?: string;
    page?: number;
    limit?: number;
    orderBy?: 'rating' | 'experience' | 'createdAt' | 'distance';
    express?: boolean;
    modality?: string;
    latitude?: number;
    longitude?: number;
    cep?: string;
    maxDistanceKm?: number;
  }): Promise<PaginatedDoctors> => {
    const searchParams = new URLSearchParams();
    if (params?.specialty) searchParams.set('specialty', params.specialty);
    if (params?.language) searchParams.set('language', params.language);
    if (params?.rating) searchParams.set('rating', String(params.rating));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.city) searchParams.set('city', params.city);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.orderBy) searchParams.set('orderBy', params.orderBy);
    if (params?.express) searchParams.set('express', 'true');
    if (params?.modality) searchParams.set('modality', params.modality);
    if (params?.latitude !== undefined) searchParams.set('latitude', String(params.latitude));
    if (params?.longitude !== undefined) searchParams.set('longitude', String(params.longitude));
    if (params?.cep) searchParams.set('cep', params.cep);
    if (params?.maxDistanceKm !== undefined) searchParams.set('maxDistanceKm', String(params.maxDistanceKm));
    const query = searchParams.toString();
    return httpClient.get<PaginatedDoctors>(`/doctors${query ? `?${query}` : ''}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    });
  },

  getFeaturedDoctors: (limit = 10): Promise<DoctorListItem[]> =>
    httpClient.get<DoctorListItem[]>(`/doctors/featured?limit=${limit}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  getSpecialties: (): Promise<Specialty[]> =>
    httpClient.get<Specialty[]>('/doctors/specialties', {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  validateCrm: (crm: string, crmState: string): Promise<{
    isValid: boolean;
    isManualReviewRequired?: boolean;
    doctorName?: string;
    crm?: string;
    crmState?: string;
    situation?: string;
    errorMessage?: string;
    infoMessage?: string;
    keyUsedName?: string;
  }> =>
    httpClient.post('/doctors/validate-crm', { crm, crmState }, {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  getDoctorBySlug: (slug: string): Promise<DoctorDetail> =>
    httpClient.get<DoctorDetail>(`/doctors/${slug}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  getMyProfile: (_doctorId?: string): Promise<DoctorProfile> =>
    httpClient.get<DoctorProfile>('/doctors/me/profile'),

  updateMyProfile: (
    doctorIdOrData?: string | Partial<DoctorProfile>,
    data?: Partial<DoctorProfile>
  ): Promise<DoctorProfile> => {
    const payload = typeof doctorIdOrData === 'string' ? data : doctorIdOrData;
    return httpClient.patch<DoctorProfile>('/doctors/me/profile', payload || {});
  },

  uploadPhoto: (doctorId: string, photoUrl: string): Promise<DoctorProfile> =>
    httpClient.post<DoctorProfile>(`/doctors/${doctorId}/photo`, { photoUrl }),

  deletePhoto: (doctorId: string): Promise<DoctorProfile> =>
    httpClient.delete<DoctorProfile>(`/doctors/${doctorId}/photo`),

  getMyEducation: (doctorId: string): Promise<DoctorEducation[]> =>
    httpClient.get<DoctorEducation[]>(`/doctors/${doctorId}/education`),

  createEducation: (doctorId: string, data: Omit<DoctorEducation, 'id'>): Promise<DoctorEducation> =>
    httpClient.post<DoctorEducation>(`/doctors/${doctorId}/education`, data),

  updateEducation: (doctorId: string, educationId: string, data: Partial<DoctorEducation>): Promise<DoctorEducation> =>
    httpClient.patch<DoctorEducation>(`/doctors/${doctorId}/education/${educationId}`, data),

  deleteEducation: (doctorId: string, educationId: string): Promise<void> =>
    httpClient.delete<void>(`/doctors/${doctorId}/education/${educationId}`),

  getMyExperience: (doctorId: string): Promise<DoctorExperience[]> =>
    httpClient.get<DoctorExperience[]>(`/doctors/${doctorId}/experience`),

  createExperience: (doctorId: string, data: Omit<DoctorExperience, 'id'>): Promise<DoctorExperience> =>
    httpClient.post<DoctorExperience>(`/doctors/${doctorId}/experience`, data),

  updateExperience: (doctorId: string, experienceId: string, data: Partial<DoctorExperience>): Promise<DoctorExperience> =>
    httpClient.patch<DoctorExperience>(`/doctors/${doctorId}/experience/${experienceId}`, data),

  deleteExperience: (doctorId: string, experienceId: string): Promise<void> =>
    httpClient.delete<void>(`/doctors/${doctorId}/experience/${experienceId}`),

  getMyAvailability: (_doctorId?: string): Promise<{
    slots: DoctorAvailabilitySlot[];
    exceptions: DoctorAvailabilityException[];
  }> =>
    httpClient.get<{ slots: DoctorAvailabilitySlot[]; exceptions: DoctorAvailabilityException[] }>(
      '/doctors/me/availability'
    ),

  updateAvailability: (
    doctorIdOrData?: string | { slots: Omit<DoctorAvailabilitySlot, 'id'>[] },
    data?: { slots: Omit<DoctorAvailabilitySlot, 'id'>[] }
  ): Promise<{ slots: DoctorAvailabilitySlot[] }> => {
    const payload = typeof doctorIdOrData === 'string' ? data : doctorIdOrData;
    return httpClient.put<{ slots: DoctorAvailabilitySlot[] }>('/doctors/me/availability', payload || { slots: [] });
  },

  createAvailabilityException: (
    _doctorId: string,
    data: Omit<DoctorAvailabilityException, 'id'>
  ): Promise<DoctorAvailabilityException> =>
    httpClient.post<DoctorAvailabilityException>('/doctors/me/availability/exceptions', data),

  deleteAvailabilityException: (_doctorId: string, exceptionId: string): Promise<void> =>
    httpClient.delete<void>(`/doctors/me/availability/exceptions/${exceptionId}`),

  getMyStatistics: (): Promise<DoctorStatistics> =>
    httpClient.get<DoctorStatistics>(`/doctors/me/statistics`),

  getMyPatientById: (patientId: string): Promise<DoctorPatientResponse> =>
    httpClient.get<DoctorPatientResponse>(`/doctors/me/patients/${patientId}`),

  getMyPatients: (page = 1, limit = 20, search?: string) => {
    let url = `/doctors/me/patients?page=${page}&limit=${limit}`;
    if (search) url += `&search=${search}`;
    return httpClient.get<{ patients: DoctorPatientResponse[]; total: number; page: number; pageSize: number }>(url);
  },

  getMySpecialties: (): Promise<DoctorSpecialty[]> =>
    httpClient.get<DoctorSpecialty[]>('/doctors/me/specialties'),

  addMySpecialty: (data: { specialtyId: string; primary?: boolean }): Promise<DoctorSpecialty> =>
    httpClient.post<DoctorSpecialty>('/doctors/me/specialties', data),

  removeMySpecialty: (specialtyId: string): Promise<{ success: boolean; message: string }> =>
    httpClient.delete<{ success: boolean; message: string }>(`/doctors/me/specialties/${specialtyId}`),

  setMyPrimarySpecialty: (specialtyId: string): Promise<DoctorSpecialty> =>
    httpClient.patch<DoctorSpecialty>(`/doctors/me/specialties/${specialtyId}/primary`),

  replaceMySpecialties: (data: { specialtyIds: string[]; primarySpecialtyId: string }): Promise<DoctorSpecialty[]> =>
    httpClient.put<DoctorSpecialty[]>('/doctors/me/specialties', data),

  submitSpecialtyProof: (specialtyId: string, data: { rqe?: string; proofDocumentUrl?: string }): Promise<DoctorSpecialty> =>
    httpClient.patch<DoctorSpecialty>(`/doctors/me/specialties/${specialtyId}/proof`, data),

  getMyDocuments: (): Promise<{ documents: DoctorDocument[]; verification: DoctorVerification | null }> =>
    httpClient.get<{ documents: DoctorDocument[]; verification: DoctorVerification | null }>('/doctors/me/documents'),

  uploadDocument: (data: { type: DocumentType; fileUrl: string }): Promise<{ success: boolean; document: DoctorDocument }> =>
    httpClient.post<{ success: boolean; document: DoctorDocument }>('/doctors/me/documents', data),

  deleteDocument: (id: string): Promise<{ success: boolean }> =>
    httpClient.delete<{ success: boolean }>(`/doctors/me/documents/${id}`),
};

export interface DoctorPatientResponse {
  id: string;
  name: string;
  email: string;
  phone?: string;
  cpf: string;
  birthDate: string;
  gender?: string;
  totalConsultations: number;
  lastConsultation?: string;
  healthConditions?: string[];
  allergies?: string[];
}

export const marketplaceApi = {
  getHome: (): Promise<MarketplaceHomeData> =>
    httpClient.get<MarketplaceHomeData>('/marketplace/home', {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  search: (params: {
    q?: string;
    specialty?: string;
    language?: string;
    rating?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginatedDoctors> => {
    const searchParams = new URLSearchParams();
    if (params.q) searchParams.set('q', params.q);
    if (params.specialty) searchParams.set('specialty', params.specialty);
    if (params.language) searchParams.set('language', params.language);
    if (params.rating) searchParams.set('rating', String(params.rating));
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return httpClient.get<PaginatedDoctors>(`/marketplace/search${query ? `?${query}` : ''}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    });
  },
};

export const patientsApi = {
  getProfile: (): Promise<PatientProfile> =>
    httpClient.get<PatientProfile>('/patients/me'),

  updateProfile: (data: {
    name?: string;
    phone?: string;
    cpf?: string;
    birthDate?: string;
    gender?: string;
    avatar?: string;
  }): Promise<PatientProfile> =>
    httpClient.put<PatientProfile>('/patients/me', data),

  exportLgpdData: (): Promise<{
    success: boolean;
    data: {
      exportedAt: string;
      patient: any;
      user: any;
      healthProfile: any;
      emergencyContact: any;
      dependents: any[];
      insurance: any[];
      appointmentsCount: number;
      medicalRecordsCount: number;
      aiConversationsCount: number;
      filesCount: number;
      recentAppointments: any[];
    };
  }> =>
    httpClient.get('/patients/me/lgpd-export'),

  anonymizeLgpdData: (): Promise<{ success: boolean; message?: string; error?: string }> =>
    httpClient.post('/patients/me/lgpd-anonymize'),

  getDependents: (page = 1, limit = 20) =>
    httpClient.get<{ dependents: DependentResponse[]; total: number; page: number; pageSize: number }>(
      `/patients/me/dependents/paginated?page=${page}&limit=${limit}`
    ),

  createDependent: (data: { name: string; cpf?: string; birthDate: string; relationship: string }): Promise<DependentResponse> =>
    httpClient.post<DependentResponse>('/patients/me/dependents', data),

  updateDependent: (id: string, data: { name?: string; cpf?: string; birthDate?: string; relationship?: string }): Promise<DependentResponse> =>
    httpClient.put<DependentResponse>(`/patients/me/dependents/${id}`, data),

  deleteDependent: (id: string): Promise<void> =>
    httpClient.delete<void>(`/patients/me/dependents/${id}`),

  getPatientAllergies: (patientId: string): Promise<PatientAllergy[]> =>
    httpClient.get<PatientAllergy[]>(`/patients/${patientId}/allergies`),
};

export interface PatientAllergy {
  id: string;
  allergen: string;
  severity?: string | null;
  reaction?: string | null;
  memedId?: number | null;
}

export interface MemedConfig {
  featureToggles: Record<string, boolean | string[]>;
  workplace: {
    city?: string;
    state?: string;
    cnes?: string;
    local_name?: string;
    address?: string;
    phone?: string;
  };
  receituarioTheme: number;
}

export const memedConfigApi = {
  getConfig: (): Promise<MemedConfig> =>
    httpClient.get<MemedConfig>('/memed/config'),
};

export interface DependentResponse {
  id: string;
  name: string;
  cpf: string;
  birthDate: string;
  relationship: string;
  phone?: string;
  email?: string;
  hasHealthProfile: boolean;
  createdAt: string;
}

export type AppointmentStatus =
  | 'CREATED'
  | 'WAITING_PAYMENT'
  | 'PAID'
  | 'WAITING_DOCTOR'
  | 'DOCTOR_ACCEPTED'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'RESCHEDULED'
  | 'COMPLETED';

export type AppointmentType = 'SCHEDULED' | 'EXPRESS' | 'FOLLOW_UP' | 'EMERGENCY' | 'RETURN';

export type SlotStatus = 'AVAILABLE' | 'BOOKED' | 'BLOCKED';

export interface AppointmentSlot {
  id: string;
  startTime: string;
  endTime: string;
  available: boolean;
  reason?: string;
}

export interface AppointmentDoctor {
  id: string;
  name: string;
  specialty: string;
  photoUrl: string | null;
  crm?: string | null;
  crmState?: string | null;
}

export interface AppointmentPatient {
  id: string;
  name: string;
  photoUrl: string | null;
  cpf?: string | null;
  email?: string | null;
}

export interface AppointmentReminder {
  id: string;
  type: string;
  scheduledFor: string;
  status: string;
}

export interface Appointment {
  id: string;
  patientId: string;
  doctorId: string;
  type: AppointmentType;
  status: AppointmentStatus;
  scheduledAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  duration: number;
  price: number;
  currency: string;
  reason: string | null;
  notes: string | null;
  cancellationReason: string | null;
  cancelledBy: string | null;
  videoRoomId: string | null;
  medicalRecordId: string | null;
  serviceType?: 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR';
  createdAt: string;
  updatedAt: string;
  doctor?: AppointmentDoctor;
  patient?: AppointmentPatient;
  reminders?: AppointmentReminder[];
  slot?: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
  };
}

export interface AppointmentListItem {
  id: string;
  type: AppointmentType;
  status: AppointmentStatus;
  serviceType?: 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR';
  scheduledAt: string;
  duration: number;
  price: number;
  doctorId?: string;
  doctor?: AppointmentDoctor;
  patient?: AppointmentPatient;
  reason?: string;
  canJoin?: boolean;
  joinUrl?: string;
  hasMedicalRecord?: boolean;
  hasReview?: boolean;
  finishedAt?: string;
}

export interface PaginatedAppointments {
  appointments: AppointmentListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AvailableSlot {
  id: string;
  startTime: string;
  endTime: string;
  available: boolean;
  reason?: string;
}

export interface AvailableDate {
  date: string;
  available?: boolean;
  reason?: string;
  holidayName?: string;
  slotsAvailable?: number;
  totalSlots?: number;
  availableSlots?: number;
  slots?: Array<{
    id?: string;
    startTime: string;
    endTime?: string;
    status?: string;
    available?: boolean;
    isBlocked?: boolean;
    blockReason?: string | null;
  }>;
}

export interface DoctorAvailabilityCalendar {
  doctorId: string;
  from: string;
  to: string;
  timezone: string;
  dates: AvailableDate[];
}

export interface ScheduleRule {
  id: string;
  ruleType: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  duration: number;
  interval: number;
  isActive: boolean;
  priority: number;
}

export interface ScheduleBlock {
  id: string;
  ruleType: string;
  startDate: string;
  endDate: string;
  reason: string;
  isActive: boolean;
}

export interface ScheduleRulesResponse {
  rules: ScheduleRule[];
  blocks: ScheduleBlock[];
}

export interface WaitingListEntry {
  id: string;
  doctorId: string;
  doctor?: {
    name: string;
    specialty: string;
  };
  status: string;
  preferredDate: string;
  preferredTimeStart: string;
  preferredTimeEnd: string;
  expiresAt: string;
  createdAt: string;
}

export interface WaitingListResponse {
  entries: WaitingListEntry[];
}

export interface Reminder {
  id: string;
  type: string;
  scheduledFor: string;
  sentAt: string | null;
  status: string;
  channel: string;
}

export interface AppointmentRemindersResponse {
  reminders: Reminder[];
}

export type FileStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED' | 'DELETED';
export type Visibility = 'PUBLIC' | 'PRIVATE' | 'SIGNED';
export type FileCategory = 'PROFILE_PHOTO' | 'COVER_IMAGE' | 'DOCUMENT' | 'CERTIFICATE' | 'MEDICAL_EXAM' | 'PRESCRIPTION' | 'CERTIFICATE_MEDICAL' | 'ATTACHMENT' | 'AUDIO' | 'VIDEO' | 'OTHER';
export type OwnerType = 'DOCTOR' | 'PATIENT' | 'APPOINTMENT' | 'MEDICAL_RECORD' | 'PRESCRIPTION' | 'CERTIFICATE' | 'EXPRESS_REQUEST';

export interface UploadedFile {
  id: string;
  ownerId: string;
  ownerType: OwnerType;
  originalName: string;
  storedName: string;
  mimeType: string;
  size: number;
  bucket: string;
  key: string;
  status: FileStatus;
  visibility: Visibility;
  category: FileCategory;
  url?: string;
  signedUrl?: string;
  expiresAt: string | null;
  metadata: Record<string, unknown>;
  checksum: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface FileAccessLog {
  id: string;
  fileId: string;
  userId: string | null;
  action: string;
  ipAddress: string;
  userAgent: string | null;
  success: boolean;
  createdAt: string;
}

export interface BatchUploadResponse {
  successful: UploadedFile[];
  failed: { index: number; error: string }[];
}

function createFormData(file: File, ownerId: string, ownerType: OwnerType, category: FileCategory, visibility: Visibility, expiresInDays?: number): FormData {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ownerId', ownerId);
  formData.append('ownerType', ownerType);
  formData.append('category', category);
  formData.append('visibility', visibility);
  if (expiresInDays) {
    formData.append('expiresInDays', String(expiresInDays));
  }
  return formData;
}

async function uploadFileToApi(
  file: File,
  ownerId: string,
  ownerType: OwnerType,
  category: FileCategory,
  visibility: Visibility = 'PRIVATE',
  expiresInDays?: number,
): Promise<UploadedFile> {
  const formData = createFormData(file, ownerId, ownerType, category, visibility, expiresInDays);

  const response = await fetch(`${httpClient['baseUrl']}/files/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errJson = await response.json().catch(() => null);
    const msg = errJson?.message || errJson?.error?.message || response.statusText || 'Erro no envio do arquivo';
    throw new Error(`Upload failed: ${msg}`);
  }

  const data = await response.json();
  return data.data;
}

export const filesApi = {
  uploadFile: async (
    file: File,
    ownerId: string,
    ownerType: OwnerType,
    category: FileCategory,
    visibility: Visibility = 'PRIVATE',
    expiresInDays?: number,
  ): Promise<UploadedFile> => {
    return uploadFileToApi(file, ownerId, ownerType, category, visibility, expiresInDays);
  },

  uploadMultipleFiles: async (
    files: File[],
    ownerId: string,
    ownerType: OwnerType,
    category: FileCategory,
    visibility: Visibility = 'PRIVATE',
    expiresInDays?: number,
  ): Promise<BatchUploadResponse> => {
    const results: BatchUploadResponse = { successful: [], failed: [] };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) {
        results.failed.push({ index: i, error: 'File is undefined' });
        continue;
      }
      try {
        const uploadedFile = await uploadFileToApi(file, ownerId, ownerType, category, visibility, expiresInDays);
        results.successful.push(uploadedFile);
      } catch (error) {
        results.failed.push({
          index: i,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    return results;
  },

  getFile: (fileId: string): Promise<UploadedFile> =>
    httpClient.get<UploadedFile>(`/files/${fileId}`),

  getSignedUrl: (fileId: string, expiresIn: number = 3600): Promise<{ url: string }> =>
    httpClient.get<{ url: string }>(`/files/${fileId}/signed-url?expiresIn=${expiresIn}`),

  downloadFile: (fileId: string): Promise<{ fileName: string; mimeType: string; size: number; buffer: string }> =>
    httpClient.get<{ fileName: string; mimeType: string; size: number; buffer: string }>(`/files/${fileId}/download`),

  deleteFile: (fileIdOrUrl: string): Promise<void> => {
    if (!fileIdOrUrl) return Promise.resolve();
    const decoded = decodeURIComponent(fileIdOrUrl);
    const cleanStr = decoded.split('?')[0] ?? '';
    const uuidMatch = cleanStr.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    const targetId = uuidMatch?.[0] ?? cleanStr.replace(/^.*\/(files\/content\/)?/, '');
    return httpClient.delete<void>(`/files/${encodeURIComponent(targetId)}`);
  },

  getFilesByOwner: (
    ownerId: string,
    ownerType: OwnerType,
    category?: FileCategory,
  ): Promise<UploadedFile[]> => {
    const params = new URLSearchParams({ ownerType });
    if (category) params.set('category', category);
    return httpClient.get<UploadedFile[]>(`/files/owner/${ownerId}?${params.toString()}`);
  },

  getFileAccessLogs: (fileId: string): Promise<FileAccessLog[]> =>
    httpClient.get<FileAccessLog[]>(`/files/${fileId}/logs`),

  updateFileMetadata: (fileId: string, metadata: Record<string, unknown>): Promise<UploadedFile> =>
    httpClient.patch<UploadedFile>(`/files/${fileId}/metadata`, { metadata }),

  getFileUrl: (fileIdOrUrl: string): string => {
    if (!fileIdOrUrl) return '';
    if (
      fileIdOrUrl.startsWith('http://') ||
      fileIdOrUrl.startsWith('https://') ||
      fileIdOrUrl.startsWith('blob:') ||
      fileIdOrUrl.startsWith('data:')
    ) {
      return fileIdOrUrl;
    }
    // Se a URL já contiver o caminho relativo de arquivos da API, garante barra inicial
    if (fileIdOrUrl.startsWith('/api/v1/files/content/')) {
      return fileIdOrUrl;
    }
    if (fileIdOrUrl.includes('/files/content/') || fileIdOrUrl.includes('/files/')) {
      return fileIdOrUrl.startsWith('/') ? fileIdOrUrl : `/${fileIdOrUrl}`;
    }
    const apiBase =
      typeof API_BASE_URL === 'string' && API_BASE_URL.startsWith('http')
        ? API_BASE_URL
        : '/api/v1';
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fileIdOrUrl);
    if (isUuid) {
      return `${apiBase}/files/${fileIdOrUrl}/download`;
    }
    const cleanKey = fileIdOrUrl.replace(/^\/+/, '');
    return `${apiBase}/files/content/${cleanKey}`;
  },
};

export type RecordStatus = 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'SIGNED' | 'CANCELLED';
export type DiagnosisType = 'PRINCIPAL' | 'SECONDARY' | 'DIFFERENTIAL';
export type PrescriptionType = 'CONTINUOUS' | 'ACUTE' | 'CONTROLLED' | 'SPECIAL';
export type ExamPriority = 'ROUTINE' | 'URGENT' | 'EMERGENCY';
export type CertificateType = 'SICK_NOTE' | 'HEALTH_CERTIFICATE' | 'TRAVEL_CERTIFICATE' | 'OTHER';
export type AttachmentType = 'LABORATORY' | 'IMAGING' | 'CLINICAL_DOCUMENT' | 'PRESCRIPTION' | 'OTHER';

export interface MedicalRecord {
  id: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  status: RecordStatus;
  anamneseId?: string;
  diagnosisIds: string[];
  prescriptionIds: string[];
  examRequestIds: string[];
  certificateIds: string[];
  evolutionIds: string[];
  attachmentIds: string[];
  signedAt?: string;
  signedBy?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Anamnese {
  id: string;
  medicalRecordId: string;
  chiefComplaint: string;
  historyOfPresentIllness: string;
  symptoms: Array<{ name: string; intensity: number; duration: string; location?: string }>;
  evolutionTime: string;
  currentMedications: Array<{ name: string; dosage: string; frequency: string; stillUsing: boolean }>;
  allergies: string[];
  previousDiseases: string[];
  surgeries: string[];
  weight?: number;
  height?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Diagnosis {
  id: string;
  medicalRecordId: string;
  cidCode?: string;
  description: string;
  type: DiagnosisType;
  isActive: boolean;
  notes?: string;
  diagnosedAt: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface PrescriptionMedication {
  name: string;
  presentation: string;
  concentration: string;
  dose: string;
  frequency: string;
  duration: string;
  quantity: number;
  observations?: string;
  // Campos ricos da MeMed
  id?: string;
  nome?: string;
  descricao?: string;
  principio_ativo?: string;
  posologia?: string;
  sanitized_posology?: string;
  quantidade?: number | string;
  unidade?: string;
  fabricante?: string;
  controle_especial?: boolean;
  receituario?: string;
  [key: string]: unknown;
}

export interface Prescription {
  id: string;
  medicalRecordId: string;
  patientId: string;
  doctorId: string;
  medications: PrescriptionMedication[];
  type: PrescriptionType;
  instructions?: string;
  validUntil?: string;
  isDigital: boolean;
  digitalSignature?: string;
  signedAt?: string;
  pdfUrl?: string;
  memedPrescriptionId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
  memedPdfUrl?: string | null;
  memedExcludedAt?: string | null;
  digitalPrescription?: {
    link?: string | null;
    unlockCode?: string | null;
  };
  unlockCode?: string | null;
  digitalPrescriptionLink?: string | null;
  content?: {
    medications?: any[];
    digitalPrescription?: {
      link?: string | null;
      unlockCode?: string | null;
    };
    [key: string]: any;
  };
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClinicalEvolution {
  id: string;
  medicalRecordId: string;
  doctorId: string;
  appointmentId: string;
  summary: string;
  conduct: string;
  observations?: string;
  nextAppointment?: string;
  isSigned: boolean;
  digitalSignature?: string;
  signedAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExamItem {
  examName: string;
  justification: string;
  observations?: string;
}

export interface ExamRequest {
  id: string;
  medicalRecordId: string;
  patientId: string;
  doctorId: string;
  appointmentId: string;
  exams: ExamItem[];
  priority: ExamPriority;
  instructions?: string;
  isDigital: boolean;
  pdfUrl?: string;
  memedId?: string;
  memedExamRequestId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED' | 'IDLE';
  memedPdfUrl?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Certificate {
  id: string;
  medicalRecordId: string;
  patientId: string;
  doctorId: string;
  appointmentId: string;
  type: CertificateType;
  startDate: string;
  endDate?: string;
  daysOff?: number;
  reason: string;
  observations?: string;
  isDigital: boolean;
  digitalSignature?: string;
  signedAt?: string;
  pdfUrl?: string;
  memedId?: string;
  memedCertificateId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
  memedPdfUrl?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Attachment {
  id: string;
  medicalRecordId: string;
  fileId: string;
  type: AttachmentType;
  description?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CID10Code {
  code: string;
  description: string;
  category: string;
}

export interface MedicalRecordAudit {
  id: string;
  medicalRecordId: string;
  userId: string;
  userType: 'PATIENT' | 'DOCTOR' | 'ADMIN';
  action: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface PaginatedMedicalRecords {
  records: MedicalRecord[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface CreateAppointmentData {
  doctorId: string;
  slotId?: string;
  scheduledAt?: string;
  type?: AppointmentType;
  reason?: string;
  notes?: string;
  serviceType?: 'TELEMEDICINA' | 'DOMICILIAR' | 'PRESENCIAL';
  dependentId?: string;
}

export const schedulingApi = {
  createAppointment: (data: CreateAppointmentData): Promise<Appointment> =>
    httpClient.post<Appointment>('/appointments', data),

  createExpressAppointment: (data: {
    specialtyId?: string;
    doctorId?: string;
    chiefComplaint?: string;
    price: number;
  }): Promise<{ appointmentId: string; doctorId?: string; status: string; price: number; currency: string }> =>
    httpClient.post<{ appointmentId: string; doctorId?: string; status: string; price: number; currency: string }>('/appointments/express', data),

  getAppointment: (appointmentId: string): Promise<Appointment> =>
    httpClient.get<Appointment>(`/appointments/${appointmentId}`),

  getPatientAppointments: (params?: {
    status?: AppointmentStatus[];
    type?: AppointmentType[];
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedAppointments> => {
    const searchParams = new URLSearchParams();
    if (params?.status) params.status.forEach(s => searchParams.append('status', s));
    if (params?.type) params.type.forEach(t => searchParams.append('type', t));
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return httpClient.get<PaginatedAppointments>(`/appointments${query ? `?${query}` : ''}`);
  },

  getDoctorAppointments: (doctorId: string, params?: {
    status?: AppointmentStatus[];
    date?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedAppointments> => {
    const searchParams = new URLSearchParams();
    if (params?.status) params.status.forEach(s => searchParams.append('status', s));
    if (params?.date) searchParams.set('date', params.date);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return httpClient.get<PaginatedAppointments>(`/doctors/${doctorId}/appointments${query ? `?${query}` : ''}`);
  },

  getMyAppointments: (params?: {
    status?: AppointmentStatus[];
    date?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedAppointments> => {
    const searchParams = new URLSearchParams();
    if (params?.status) params.status.forEach(s => searchParams.append('status', s));
    if (params?.date) searchParams.set('date', params.date);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return httpClient.get<PaginatedAppointments>(`/doctors/me/appointments${query ? `?${query}` : ''}`);
  },

  getUpcomingAppointments: (limit = 10): Promise<{ appointments: AppointmentListItem[] }> =>
    httpClient.get<{ appointments: AppointmentListItem[] }>(`/appointments/upcoming?limit=${limit}`),

  getAppointmentHistory: (params?: {
    limit?: number;
    offset?: number;
  }): Promise<PaginatedAppointments> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return httpClient.get<PaginatedAppointments>(`/appointments/history${query ? `?${query}` : ''}`);
  },

  updateAppointment: (
    appointmentId: string,
    data: { reason?: string; notes?: string; internalNotes?: string }
  ): Promise<Appointment> =>
    httpClient.patch<Appointment>(`/appointments/${appointmentId}`, data),

  cancelAppointment: (appointmentId: string, reason: string): Promise<{
    id: string;
    status: AppointmentStatus;
    cancelledBy: string;
    cancellationReason: string;
    refundAmount: number;
    refundStatus: string;
    cancelledAt: string;
  }> =>
    httpClient.delete(`/appointments/${appointmentId}`, { data: { reason } }),

  confirmAppointment: (appointmentId: string): Promise<{
    id: string;
    status: AppointmentStatus;
    confirmedAt: string;
  }> =>
    httpClient.post(`/appointments/${appointmentId}/confirm`),

  startAppointment: (appointmentId: string): Promise<{
    id: string;
    status: AppointmentStatus;
    startedAt: string;
    videoRoom?: {
      id: string;
      roomUrl: string;
      token: string;
    };
  }> =>
    httpClient.post(`/appointments/${appointmentId}/start`),

  finishAppointment: (appointmentId: string, data: { duration?: number; notes?: string }): Promise<{
    id: string;
    status: AppointmentStatus;
    startedAt: string;
    finishedAt: string;
    duration: number;
    medicalRecordId: string;
  }> =>
    httpClient.post(`/appointments/${appointmentId}/finish`, data),

  rescheduleAppointment: (appointmentId: string, newSlotId: string): Promise<{
    originalAppointment: {
      id: string;
      status: AppointmentStatus;
      scheduledAt: string;
    };
    newAppointment: {
      id: string;
      status: AppointmentStatus;
      scheduledAt: string;
      slot: {
        id: string;
        date: string;
        startTime: string;
        endTime: string;
      };
    };
    rescheduledAt: string;
  }> =>
    httpClient.post(`/appointments/${appointmentId}/reschedule`, { newSlotId }),

  getAvailableSlots: (doctorId: string, date: string): Promise<{
    doctorId: string;
    date: string;
    timezone: string;
    slots: AvailableSlot[];
    totalSlots: number;
    availableSlots: number;
    isHoliday: boolean;
    isWeekend: boolean;
  }> =>
    httpClient.get(`/doctors/${doctorId}/slots?date=${date}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  getAvailabilityCalendar: (doctorId: string, from: string, to: string): Promise<DoctorAvailabilityCalendar> =>
    httpClient.get(`/doctors/${doctorId}/availability?from=${from}&to=${to}`, {
      requiresAuth: false,
      skipAutoRefresh: true,
    }),

  updateAvailability: (data: {
    rules: Array<{
      dayOfWeek: number;
      isActive: boolean;
      startTime: string;
      endTime: string;
      duration?: number;
      interval?: number;
      priority?: number;
    }>;
    clearExisting?: boolean;
  }): Promise<{
    rulesCreated: number;
    rulesUpdated: number;
    rulesDeactivated: number;
    slotsGenerated: number;
    effectiveFrom: string;
  }> =>
    httpClient.put('/doctors/availability', data),

  blockTime: (data: {
    type: string;
    startDate: string;
    endDate: string;
    reason?: string;
    message?: string;
  }): Promise<{
    blockId: string;
    type: string;
    startDate: string;
    endDate: string;
    affectedAppointments: number;
    actionsRequired: Array<{
      appointmentId: string;
      patientName: string;
      action: string;
    }>;
  }> =>
    httpClient.post('/doctors/availability/block', data),

  getScheduleRules: (doctorId: string): Promise<ScheduleRulesResponse> =>
    httpClient.get(`/doctors/${doctorId}/schedule-rules`),

  joinWaitingList: (data: {
    doctorId: string;
    appointmentType?: AppointmentType;
    preferredDate?: string;
    preferredTimeStart?: string;
    preferredTimeEnd?: string;
  }): Promise<WaitingListEntry> =>
    httpClient.post('/waiting-list', data),

  getMyWaitingList: (): Promise<WaitingListResponse> =>
    httpClient.get('/waiting-list/me'),

  leaveWaitingList: (entryId: string): Promise<{ id: string; status: string }> =>
    httpClient.delete(`/waiting-list/${entryId}`),

  getAppointmentReminders: (appointmentId: string): Promise<AppointmentRemindersResponse> =>
    httpClient.get(`/appointments/${appointmentId}/reminders`),

  createFollowUp: (appointmentId: string, data: { scheduledAt: string; duration?: number; notes?: string }): Promise<Appointment> =>
    httpClient.post<Appointment>(`/appointments/${appointmentId}/follow-up`, data),

  getFollowUps: (appointmentId: string): Promise<Appointment[]> =>
    httpClient.get<Appointment[]>(`/appointments/${appointmentId}/follow-ups`),

  getOriginalAppointment: (followUpId: string): Promise<{ success: boolean; data?: Appointment; message?: string }> =>
    httpClient.get<{ success: boolean; data?: Appointment; message?: string }>(`/appointments/follow-up/${followUpId}/original`),
};

export const medicalRecordsApi = {
  createMedicalRecord: (data: {
    appointmentId: string;
    patientId: string;
    doctorId: string;
  }): Promise<MedicalRecord> =>
    httpClient.post<MedicalRecord>('/medical-records', data),

  getMedicalRecord: (recordId: string): Promise<MedicalRecord> =>
    httpClient.get<MedicalRecord>(`/medical-records/${recordId}`),

  getMedicalRecordByAppointment: (appointmentId: string): Promise<MedicalRecord> =>
    httpClient.get<MedicalRecord>(`/medical-records/appointment/${appointmentId}`),

  getMedicalRecords: (params?: {
    patientId?: string;
    doctorId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedMedicalRecords> => {
    const searchParams = new URLSearchParams();
    if (params?.patientId) searchParams.set('patientId', params.patientId);
    if (params?.doctorId) searchParams.set('doctorId', params.doctorId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return httpClient.get<PaginatedMedicalRecords>(`/medical-records${query ? `?${query}` : ''}`);
  },

  getPatientMedicalHistory: (patientId: string): Promise<MedicalRecord[]> =>
    httpClient.get<{ records: MedicalRecord[] }>(`/medical-records?patientId=${patientId}`).then(res => res.records || []),

  getDoctorMedicalRecords: (doctorId: string): Promise<MedicalRecord[]> =>
    httpClient.get<MedicalRecord[]>(`/medical-records?doctorId=${doctorId}`),

  openMedicalRecord: (recordId: string, data: { openedBy: string; userType: 'PATIENT' | 'DOCTOR' | 'ADMIN' }): Promise<MedicalRecord> =>
    httpClient.patch<MedicalRecord>(`/medical-records/${recordId}/open`, data),

  signMedicalRecord: (recordId: string, data: { signedBy: string; signature: string }): Promise<MedicalRecord> =>
    httpClient.patch<MedicalRecord>(`/medical-records/${recordId}/sign`, data),

  createAnamnese: (data: {
    medicalRecordId: string;
    chiefComplaint: string;
    historyOfPresentIllness: string;
    symptoms: Array<{ name: string; intensity: number; duration: string; location?: string }>;
    evolutionTime: string;
    currentMedications: Array<{ name: string; dosage: string; frequency: string; stillUsing: boolean }>;
    allergies: string[];
    previousDiseases: string[];
    surgeries: string[];
    weight?: number;
    height?: number;
  }): Promise<Anamnese> =>
    httpClient.post<Anamnese>('/medical-records/anamnese', data),

  createDiagnosis: (data: {
    medicalRecordId: string;
    cidCode?: string;
    description: string;
    type: DiagnosisType;
  }): Promise<Diagnosis> =>
    httpClient.post<Diagnosis>('/medical-records/diagnosis', data),

  createPrescription: (data: {
    medicalRecordId?: string;
    patientId: string;
    doctorId: string;
    medications: PrescriptionMedication[];
    type: PrescriptionType;
    instructions?: string;
    memedPrescriptionId?: string;
  }): Promise<Prescription> =>
    httpClient.post<Prescription>('/medical-records/prescription', data),

  createClinicalEvolution: (data: {
    medicalRecordId: string;
    doctorId: string;
    appointmentId: string;
    summary: string;
    conduct: string;
    observations?: string;
    nextAppointment?: string;
  }): Promise<ClinicalEvolution> =>
    httpClient.post<ClinicalEvolution>('/medical-records/clinical-evolution', data),

  createExamRequest: (data: {
    medicalRecordId?: string;
    patientId: string;
    doctorId: string;
    appointmentId?: string;
    exams: ExamItem[];
    priority: ExamPriority;
    instructions?: string;
  }): Promise<ExamRequest> =>
    httpClient.post<ExamRequest>('/medical-records/exam-request', data),

  createCertificate: (data: {
    medicalRecordId?: string;
    patientId: string;
    doctorId: string;
    appointmentId?: string;
    type: CertificateType;
    startDate: string;
    endDate?: string;
    daysOff?: number;
    reason: string;
    observations?: string;
  }): Promise<Certificate> =>
    httpClient.post<Certificate>('/medical-records/certificate', data),

  searchCID10: (query: string): Promise<CID10Code[]> =>
    httpClient.get<CID10Code[]>(`/cid10/search?q=${encodeURIComponent(query)}`),

  getCID10ByCode: (code: string): Promise<CID10Code> =>
    httpClient.get<CID10Code>(`/cid10/${code}`),

  getMedicalRecordAudit: (recordId: string): Promise<MedicalRecordAudit[]> =>
    httpClient.get<MedicalRecordAudit[]>(`/medical-records/${recordId}/audit`),

  updateMedicalRecord: (recordId: string, data: { status?: string }): Promise<MedicalRecord> =>
    httpClient.patch<MedicalRecord>(`/medical-records/${recordId}`, data),

  closeMedicalRecord: (recordId: string, closedBy: string): Promise<MedicalRecord> =>
    httpClient.patch<MedicalRecord>(`/medical-records/${recordId}/close`, { closedBy }),

  getMedicalRecordTimeline: (recordId: string): Promise<Array<{ type: string; date: string; description: string }>> =>
    httpClient.get<Array<{ type: string; date: string; description: string }>>(`/medical-records/${recordId}/timeline`),

  getDiagnosesByMedicalRecord: (recordId: string): Promise<Diagnosis[]> =>
    httpClient.get<Diagnosis[]>(`/medical-records/${recordId}/diagnoses`),

  getPrescriptionsByMedicalRecord: (recordId: string): Promise<Prescription[]> =>
    httpClient.get<Prescription[]>(`/medical-records/${recordId}/prescriptions`),

  getExamRequestsByMedicalRecord: (recordId: string): Promise<ExamRequest[]> =>
    httpClient.get<ExamRequest[]>(`/medical-records/${recordId}/exam-requests`),

  getClinicalEvolutionsByMedicalRecord: (recordId: string): Promise<ClinicalEvolution[]> =>
    httpClient.get<ClinicalEvolution[]>(`/medical-records/${recordId}/clinical-evolutions`),

  getCertificatesByMedicalRecord: (recordId: string): Promise<Certificate[]> =>
    httpClient.get<Certificate[]>(`/medical-records/${recordId}/certificates`),

  updateDiagnosis: (diagnosisId: string, data: { description?: string; cidCode?: string; type?: string; isActive?: boolean }): Promise<Diagnosis> =>
    httpClient.patch<Diagnosis>(`/medical-records/diagnosis/${diagnosisId}`, data),

  deleteDiagnosis: (diagnosisId: string): Promise<void> =>
    httpClient.delete<void>(`/medical-records/diagnosis/${diagnosisId}`),

  deletePrescription: (prescriptionId: string): Promise<void> =>
    httpClient.delete<void>(`/medical-records/prescription/${prescriptionId}`),

  cancelPrescription: (prescriptionId: string, _reason?: string): Promise<{ success: boolean; message: string }> =>
    httpClient.delete<{ success: boolean; message: string }>(`/medical-records/prescriptions/${prescriptionId}/cancelar`),

  linkMemedPrescription: (prescriptionId: string, memedPrescriptionId: string): Promise<{ id: string; memedPrescriptionId: string }> =>
    httpClient.patch<{ id: string; memedPrescriptionId: string }>(`/prescriptions/${prescriptionId}/memed`, { memedPrescriptionId }),

  syncMemedPrescription: (payload: {
    appointmentId?: string;
    medicalRecordId?: string;
    patientId: string;
    doctorId?: string;
    memedPrescriptionId: string;
    medications?: any[];
    digitalPrescriptionLink?: string;
    unlockCode?: string;
    doctorToken?: string;
  }): Promise<Prescription> =>
    httpClient.post<Prescription>('/medical-records/prescriptions/sync-memed', payload),

  ingestPrescriptionAnalytics: (data: {
    prescriptionUuid: string;
    pacienteId: string;
    medicoId: string;
    medicamentos: any[];
  }): Promise<any> =>
    httpClient.post<any>('/medical-records/prescriptions/analytics', data),

  getPrescriptionCostSummary: (prescriptionUuid: string): Promise<any> =>
    httpClient.get<any>(`/medical-records/prescriptions/${prescriptionUuid}/cost-summary`),

  getPrescriptionTissGuide: (prescriptionUuid: string): Promise<any> =>
    httpClient.get<any>(`/medical-records/prescriptions/${prescriptionUuid}/guia-tiss`),

  getDigitalPrescriptionLink: (prescriptionId: string): Promise<{ link: string; unlockCode: string }> =>
    httpClient.get<{ link: string; unlockCode: string }>(`/medical-records/prescriptions/${prescriptionId}/digital-link`),

  markMemedPrescriptionExcluded: (prescriptionId: string): Promise<{ id: string; memedExcludedAt: string | null }> =>
    httpClient.patch<{ id: string; memedExcludedAt: string | null }>(`/prescriptions/${prescriptionId}/memed-excluded`, {}),

  deleteExamRequest: (examRequestId: string): Promise<void> =>
    httpClient.delete<void>(`/medical-records/exam-request/${examRequestId}`),

  attachExamFile: (examId: string, data: { fileUrl: string; fileName: string; fileType: string; fileSize?: number }): Promise<any> =>
    httpClient.post<any>(`/medical-records/exam-request/${examId}/attachments`, data),

  getExamAttachments: (examId: string): Promise<any[]> =>
    httpClient.get<any[]>(`/medical-records/exam-request/${examId}/attachments`),

  deleteExamAttachment: (attachmentId: string): Promise<void> =>
    httpClient.delete<void>(`/medical-records/exam-request/attachments/${attachmentId}`),

  updateExamResult: (examId: string, result: any): Promise<any> =>
    httpClient.patch<any>(`/medical-records/exam-request/${examId}/result`, { result }),

  downloadPrescriptionPdf: async (prescriptionId: string): Promise<{ pdf: string; mimeType: string }> => {
    const data = await httpClient.get<{ pdf: string; mimeType: string }>(`/medical-records/prescription/${prescriptionId}/pdf`);
    return data;
  },

  downloadCertificatePdf: async (certificateId: string): Promise<{ pdf: string; mimeType: string }> => {
    const data = await httpClient.get<{ pdf: string; mimeType: string }>(`/medical-records/certificate/${certificateId}/pdf`);
    return data;
  },

  getCertificates: (patientId: string, page = 1, limit = 20): Promise<{ data: Certificate[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: Certificate[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/certificates?patientId=${patientId}&page=${page}&limit=${limit}`),

  getExamRequests: (patientId: string, page = 1, limit = 20): Promise<{ data: ExamRequest[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: ExamRequest[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/exam-requests?patientId=${patientId}&page=${page}&limit=${limit}`),

  getPrescriptions: (patientId: string, page = 1, limit = 20): Promise<{ data: Prescription[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: Prescription[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/prescriptions?patientId=${patientId}&page=${page}&limit=${limit}`),

  getDoctorCertificates: (_doctorId: string, page = 1, limit = 20): Promise<{ data: Certificate[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: Certificate[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/doctors/me/certificates?page=${page}&limit=${limit}`),

  getDoctorPrescriptions: (_doctorId: string, page = 1, limit = 20): Promise<{ data: Prescription[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: Prescription[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/doctors/me/prescriptions?page=${page}&limit=${limit}`),

  getDoctorExamRequests: (_doctorId: string, page = 1, limit = 20): Promise<{ data: ExamRequest[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: ExamRequest[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/doctors/me/exam-requests?page=${page}&limit=${limit}`),

  getPatientCertificates: (page = 1, limit = 20): Promise<{ data: Certificate[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: Certificate[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/patient/me/certificates?page=${page}&limit=${limit}`),

  getPatientPrescriptions: (page = 1, limit = 20): Promise<{ data: Prescription[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: Prescription[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/patient/me/prescriptions?page=${page}&limit=${limit}`),

  getPatientExamRequests: (page = 1, limit = 20): Promise<{ data: ExamRequest[]; meta: { page: number; limit: number; total: number; pages: number } }> =>
    httpClient.get<{ data: ExamRequest[]; meta: { page: number; limit: number; total: number; pages: number } }>(`/medical-records/patient/me/exam-requests?page=${page}&limit=${limit}`),

  downloadExamRequestPdf: async (examId: string): Promise<{ pdf: string; mimeType: string }> => {
    const data = await httpClient.get<{ pdf: string; mimeType: string }>(`/medical-records/exam-request/${examId}/pdf`);
    return data;
  },
};

export interface AdminAppointment {
  id: string;
  type: string;
  serviceType?: string;
  status: string;
  scheduledAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  duration?: number;
  price: number;
  displacementFee?: number;
  reason?: string | null;
  notes?: string | null;
  cancellationReason?: string | null;
  cancelledBy?: string | null;
  cancelledAt?: string | null;
  patientId?: string;
  patientName: string;
  patientEmail: string;
  patientPhone?: string | null;
  patientCpf?: string | null;
  dependentName?: string | null;
  dependentRelationship?: string | null;
  doctorId?: string;
  doctorName: string;
  doctorEmail?: string | null;
  doctorCrm?: string | null;
  doctorSpecialty: string;
  hasPayment: boolean;
  paymentId?: string | null;
  paymentStatus: string;
  paymentMethod?: string | null;
  gatewayId?: string | null;
  medicalRecordId?: string | null;
  medicalRecordStatus?: string | null;
  videoRoomId?: string | null;
  telemedicineSessionId?: string | null;
  prescriptions?: Array<{
    id: string;
    memedPrescriptionId?: string | null;
    memedPdfUrl?: string | null;
    memedStatus?: string | null;
    createdAt: string;
  }>;
  certificates?: Array<{
    id: string;
    pdfUrl?: string | null;
    diagnosis?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    createdAt: string;
  }>;
  exams?: Array<{
    id: string;
    type?: string | null;
    status?: string | null;
    description?: string | null;
  }>;
  createdAt: string;
}

export interface AdminAppointmentsResponse {
  data: AdminAppointment[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    pages: number;
  };
}

export const adminApi = {
  getAppointments: (params: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    serviceType?: string;
    search?: string;
    doctorId?: string;
    patientId?: string;
  }): Promise<AdminAppointmentsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.status) searchParams.set('status', params.status);
    if (params.type) searchParams.set('type', params.type);
    if (params.serviceType) searchParams.set('serviceType', params.serviceType);
    if (params.search) searchParams.set('search', params.search);
    if (params.doctorId) searchParams.set('doctorId', params.doctorId);
    if (params.patientId) searchParams.set('patientId', params.patientId);
    const query = searchParams.toString();
    return httpClient.get<AdminAppointmentsResponse>(`/admin/appointments${query ? `?${query}` : ''}`);
  },

  cancelAppointment: (id: string, reason?: string): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> =>
    httpClient.delete<{ success: boolean; message: string; data?: any }>(
      `/admin/appointments/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`
    ),

  refundAppointment: (id: string, reason?: string, amount?: number): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> =>
    httpClient.post<{ success: boolean; message: string; data?: any }>(
      `/admin/appointments/${id}/refund`,
      { reason, amount }
    ),

  getDoctorSpecialties: (doctorId: string): Promise<DoctorSpecialty[]> =>
    httpClient.get<DoctorSpecialty[]>(`/admin/doctors/${doctorId}/specialties`),

  addSpecialtyToDoctor: (doctorId: string, specialtyId: string, primary = false): Promise<DoctorSpecialty> =>
    httpClient.post<DoctorSpecialty>(`/admin/doctors/${doctorId}/specialties`, { specialtyId, primary }),

  removeSpecialtyFromDoctor: (doctorId: string, specialtyId: string): Promise<{ success: boolean; message: string }> =>
    httpClient.delete<{ success: boolean; message: string }>(`/admin/doctors/${doctorId}/specialties/${specialtyId}`),

  setDoctorPrimarySpecialty: (doctorId: string, specialtyId: string): Promise<DoctorSpecialty> =>
    httpClient.patch<DoctorSpecialty>(`/admin/doctors/${doctorId}/specialties/${specialtyId}/primary`),

  // Consulta CRM Key Pool & Settings
  getCrmConfig: (): Promise<{
    data: {
      enabled: boolean;
      activeKeyIndex: number;
      rotationStrategy: string;
      totalKeys: number;
      activeKeysCount: number;
      keys: Array<{
        id: string;
        name: string;
        key: string;
        maskedKey: string;
        isActive: boolean;
        quota?: number;
        usage?: number;
        lastUsedAt?: string;
        lastError?: string | null;
      }>;
    };
  }> => httpClient.get('/admin/config/crm'),

  toggleCrmValidation: (enabled: boolean): Promise<any> =>
    httpClient.patch('/admin/config/crm/toggle', { enabled }),

  addCrmKey: (data: { name: string; key: string; quota?: number }): Promise<any> =>
    httpClient.post('/admin/config/crm/keys', data),

  updateCrmKey: (id: string, data: { name?: string; isActive?: boolean; quota?: number }): Promise<any> =>
    httpClient.patch(`/admin/config/crm/keys/${id}`, data),

  deleteCrmKey: (id: string): Promise<any> =>
    httpClient.delete(`/admin/config/crm/keys/${id}`),

  testCrmKey: (data: { key?: string; crm: string; crmState: string }): Promise<any> =>
    httpClient.post('/admin/config/crm/test', data),

  getDoctorDocuments: (doctorId: string): Promise<{ documents: DoctorDocument[]; verification: DoctorVerification | null; doctor: any }> =>
    httpClient.get<{ documents: DoctorDocument[]; verification: DoctorVerification | null; doctor: any }>(`/admin/doctors/${doctorId}/documents`),

  verifyDoctorDocument: (doctorId: string, docId: string, data: { status: 'APPROVED' | 'REJECTED'; rejectionReason?: string }): Promise<{ success: boolean; document: DoctorDocument }> =>
    httpClient.patch<{ success: boolean; document: DoctorDocument }>(`/admin/doctors/${doctorId}/documents/${docId}/verify`, data),

  verifyDoctorSpecialty: (doctorId: string, specialtyId: string, data: { approved: boolean; rejectionReason?: string; rqe?: string; telemedicineEnabled?: boolean; inPersonEnabled?: boolean; homeCareEnabled?: boolean }): Promise<DoctorSpecialty> =>
    httpClient.patch<DoctorSpecialty>(`/admin/doctors/${doctorId}/specialties/${specialtyId}/verify`, data),

  getHomeCareConfig: () => httpClient.get<HomeCareConfigResponse>('/admin/config/home-care'),
  updateHomeCareConfig: (data: { basePrice?: number; displacementRules?: HomeCareDisplacementRule[] }) => httpClient.put<HomeCareConfigResponse>('/admin/config/home-care', data),
  getExpressPricing: () => httpClient.get<{ success: boolean; data: any }>('/admin/config/express-pricing'),
  updateExpressPricing: (data: any) => httpClient.put<{ success: boolean; data: any }>('/admin/config/express-pricing', data),

  getChatwootConfig: () => httpClient.get<{ success: boolean; data: any }>('/admin/config/chatwoot'),
  updateChatwootConfig: (data: any) => httpClient.put<{ success: boolean; data: any }>('/admin/config/chatwoot', data),
  testChatwootConnection: () => httpClient.post<{ success: boolean; message: string }>('/admin/config/chatwoot/test-connection'),
  testChatwootDispatch: (data: any) => httpClient.post<{ success: boolean; messageId: number }>('/admin/config/chatwoot/test-dispatch', data),

  getDoctorPriorityConfig: (): Promise<{
    success: boolean;
    config: {
      enabled: boolean;
      priorityList: Array<{ doctorId: string; order: number; badge?: string; isPinned: boolean }>;
    };
    doctors: Array<{
      id: string;
      name: string;
      email: string;
      crm: string;
      crmState: string;
      specialty: string;
      photoUrl: string | null;
      rating: number;
      totalReviews: number;
    }>;
  }> => httpClient.get('/admin/doctors/priority-config'),

  updateDoctorPriorityConfig: (data: {
    enabled?: boolean;
    priorityList: Array<{ doctorId: string; order: number; badge?: string; isPinned: boolean }>;
  }): Promise<{
    success: boolean;
    config: {
      enabled: boolean;
      priorityList: Array<{ doctorId: string; order: number; badge?: string; isPinned: boolean }>;
    };
  }> => httpClient.put('/admin/doctors/priority-config', data),

  impersonateDoctor: (doctorId: string): Promise<{
    success: boolean;
    accessToken: string;
    user: any;
    impersonatedBy: {
      adminId: string;
      adminEmail: string;
    };
  }> => httpClient.post(`/admin/doctors/${doctorId}/impersonate`),

  // Módulo Motor de Vendas & Banners Globais
  getBanners: () => httpClient.get<{ success: boolean; data: any }>('/admin/banners'),
  createBanner: (data: any) => httpClient.post<{ success: boolean; data: any }>('/admin/banners', data),
  updateBanner: (id: string, data: any) => httpClient.put<{ success: boolean; data: any }>(`/admin/banners/${id}`, data),
  deleteBanner: (id: string) => httpClient.delete<{ success: boolean }>(`/admin/banners/${id}`),
  toggleBanner: (id: string, active?: boolean) => httpClient.patch<{ success: boolean; data: any }>(`/admin/banners/${id}/toggle`, { active }),
  reorderBanners: (orderList: Array<{ id: string; order: number }>) => httpClient.patch<{ success: boolean; data: any[] }>('/admin/banners/reorder', { orderList }),
  updateBannerSettings: (settings: any) => httpClient.put<{ success: boolean; data: any }>('/admin/banners/settings', settings),
};

export const bannersApi = {
  getActiveBanners: (role?: string, placement?: 'LANDING' | 'APP') => {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (placement) params.append('placement', placement);
    const qs = params.toString();
    return httpClient.get<{
      success: boolean;
      data: {
        globalAutoplay: boolean;
        defaultSlideDurationSeconds: number;
        allowDismissForSession: boolean;
        banners: any[];
      };
    }>(`/banners/active${qs ? `?${qs}` : ''}`);
  },
  trackView: (id: string) => httpClient.post<{ success: boolean }>(`/banners/${id}/view`),
  trackClick: (id: string) => httpClient.post<{ success: boolean }>(`/banners/${id}/click`),
};

export const supportBotApi = {
  checkEligibility: () =>
    httpClient.get<{
      success: boolean;
      data: {
        isEligible: boolean;
        paidCount: number;
        botName: string;
        isBusinessHours: boolean;
      };
    }>('/support/bot/eligibility'),
  sendMessage: (message: string) =>
    httpClient.post<{
      success: boolean;
      data: {
        message: string;
        options?: Array<{ label: string; action: string }>;
        isHumanTransferred?: boolean;
        ticketCreatedId?: string;
      };
    }>('/support/bot/chat', { message }),
};

export interface SupportTicketItem {
  id: string;
  userId: string;
  subject: string;
  description: string;
  category?: string | null;
  priority: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  assignedTo?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const supportTicketsApi = {
  getMyTickets: () =>
    httpClient.get<{ success: boolean; data: SupportTicketItem[] }>('/support/tickets'),
  createTicket: (data: { subject: string; description: string; category?: string; priority?: string }) =>
    httpClient.post<{ success: boolean; data: SupportTicketItem }>('/support/tickets', data),
  getTicketById: (id: string) =>
    httpClient.get<{ success: boolean; data: SupportTicketItem }>(`/support/tickets/${id}`),

  // Visão Admin
  adminListTickets: (params?: { page?: number; limit?: number; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.append('page', String(params.page));
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.status) qs.append('status', params.status);
    const query = qs.toString();
    return httpClient.get<{
      success: boolean;
      data: SupportTicketItem[];
      meta?: { page: number; limit: number; total: number; pages: number };
    }>(`/admin/support/tickets${query ? `?${query}` : ''}`);
  },
  adminUpdateTicket: (id: string, data: { status?: string; assignedTo?: string }) =>
    httpClient.patch<{ success: boolean; data: SupportTicketItem }>(`/admin/support/tickets/${id}`, data),
  adminResolveTicket: (id: string) =>
    httpClient.post<{ success: boolean; data: SupportTicketItem }>(`/admin/support/tickets/${id}/resolve`),
};

export interface HomeCareDisplacementRule {
  region: string;
  cepPrefix: string;
  fee: number;
}

export interface HomeCareConfigResponse {
  basePrice: number;
  displacementRules: HomeCareDisplacementRule[];
}

export interface ExameCatalogItem {
  id: string;
  nome: string;
  nomeNormalizado: string;
  sinonimos?: string[];
  codigoTuss?: string;
  codigoSigtap?: string;
  memedId?: string;
  categoria: string;
  tipoAmostra?: string;
  preparoGeral?: string;
}

export interface IngestMemedExamsPayload {
  pacienteId: string;
  medicoId: string;
  prescriptionUuid?: string;
  pdfUrl?: string;
  signed?: boolean;
  appointmentId?: string;
  medicalRecordId?: string;
  observacoesGerais?: string;
  exames: {
    memedId?: string;
    nome: string;
    tussCodigo?: string;
    susCodigo?: string;
    indicacaoClinica?: string;
    preparoInstrucoes?: string;
  }[];
}

export interface LabQuoteItem {
  laboratorioId: string;
  nomeFantasia: string;
  cnpj: string;
  coberturaCompleta: boolean;
  coberturaPercentual: number;
  examesAtendidos: number;
  totalItens: number;
  valorTotalExames: number;
  taxaDomiciliar: number;
  valorTotalComDomiciliar: number;
  prazoResultadoHoras: number;
  prazoResultadoDias: number;
  aceitaDomiciliar: boolean;
  detalhesItens: {
    nome: string;
    tussCodigo?: string;
    coberto: boolean;
    mnemonico: string;
    precoParticular: number;
    prazoHoras: number;
  }[];
}

export interface OrderQuotesResponse {
  pedidoId: string;
  dataEmissao: string;
  totalExames: number;
  itens: {
    id: string;
    nome: string;
    tussCodigo?: string;
    preparo?: string;
  }[];
  laboratorios: LabQuoteItem[];
}

export interface ScheduleExamPayload {
  laboratorioId: string;
  tipoColeta: 'DOMICILIAR' | 'POSTO_COLETA';
  dataHoraAgendada: string;
  enderecoColeta?: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    uf: string;
  };
  observacoes?: string;
}

export interface PatientExamOrder {
  id: string;
  prescriptionUuid?: string;
  memedPdfUrl?: string;
  assinadoDigitalmente: boolean;
  status: string;
  dataEmissao: string;
  observacoesGerais?: string;
  medico: {
    id: string;
    nome: string;
    crm: string;
    especialidade: string;
  };
  totalExames: number;
  itens: {
    id: string;
    nome: string;
    tussCodigo?: string;
    susCodigo?: string;
    indicacaoClinica?: string;
    preparoInstrucoes?: string;
    status: string;
  }[];
  agendamento?: {
    id: string;
    laboratorioNome: string;
    tipoColeta: 'DOMICILIAR' | 'POSTO_COLETA';
    dataHoraAgendada: string;
    protocolo?: string;
    valorTotal: number;
    status: string;
  } | null;
}

export const examsHubApi = {
  searchCatalog: (q = '', categoria?: string, limit = 30) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (categoria) params.set('categoria', categoria);
    if (limit) params.set('limit', String(limit));
    return httpClient.get<ExameCatalogItem[]>(`/exames/catalogo?${params.toString()}`);
  },

  ingestExams: (data: IngestMemedExamsPayload) =>
    httpClient.post<any>('/exames/ingestao', data),

  quoteLaboratories: (pedidoId: string) =>
    httpClient.get<OrderQuotesResponse>(`/pedidos-exames/${pedidoId}/orcamento-laboratorios`),

  scheduleExam: (pedidoId: string, data: ScheduleExamPayload) =>
    httpClient.post<any>(`/pedidos-exames/${pedidoId}/agendar`, data),

  getPatientOrders: () =>
    httpClient.get<PatientExamOrder[]>('/pedidos-exames/paciente'),

  getOrderById: (pedidoId: string) =>
    httpClient.get<any>(`/pedidos-exames/${pedidoId}`),
};



